// @ts-check
import { z } from 'zod';

import Timestamp from '@/store/modules/models/global/Timestamp';

import { partyStatusSchema } from './partyStatus';

export const partySchema = z.object({
  id: z.number(),
  envelope_id: z.number(),
  party_envelope_id: z.number(),
  title: z.string(),
  email: z.string(),
  order: z.number(),
  role: z.string(),
  message: z.string().optional(),
  password: z.string().optional(),
  preview_url: z.string(),
  signing_url: z.string(),
  template_party_ids: z.array(z.number()).nullable(),
  status: partyStatusSchema,
});
export const partiesSchema = partySchema.array();

export const creatingPartySchema = partySchema
  .pick({
    title: true,
    email: true,
    order: true,
    role: true,
    message: true,
    password: true,
    status: true,
  })
  .extend({
    id: partySchema.shape.id.optional(),
    template_party_ids: partySchema.shape.template_party_ids.optional(),
  })
  .transform((party) => {
    const transformed = {
      ...party,
      message: party.message || undefined,
      password: party.password || undefined,
      role_ids: party.template_party_ids || [],
    };
    delete transformed.template_party_ids;

    return transformed;
  });
export const creatingPartiesSchema = creatingPartySchema.array();

export const savedPartySchema = partySchema
  .pick({
    id: true,
    title: true,
    email: true,
    order: true,
    role: true,
    message: true,
    password: true,
  })
  .extend({
    role_ids: z.array(z.number()),
  });
export const savedPartiesSchema = savedPartySchema.array();

export const partyUpdateSchema = z
  .object({
    id: z.number().optional(),
    title: z.string().min(1, 'ui.envelopes.create.validation.nameRequired'),
    email: z
      .string()
      .min(1, 'ui.envelopes.create.validation.emailRequired')
      .email('ui.envelopes.create.validation.emailInvalid'),
    order: z.number(),
    role: z.string(),
    message: z
      .string()
      .min(1, 'MessageRequired')
      .optional(),
    password: z
      .string()
      .min(1, 'PasswordRequired')
      .max(20, 'ui.envelopes.create.validation.passwordTooLong')
      .optional(),
    role_ids: z.array(z.number()),
  })
  .transform(party => ({
    id: 'id' in party ? party.id : undefined,
    title: party.title,
    email: party.email,
    order: party.order,
    role: party.role,
    message: party.message,
    password: party.password,
    template_party_ids: party.role_ids,
  }));
export const partiesUpdateSchema = z.object({
  parties: partyUpdateSchema.array(),
  parallel_signing: z.boolean(),
  envelope_progressed_at: Timestamp.optional(),
});

/** @typedef {z.infer<typeof creatingPartySchema>} TCreatingParty */
/** @typedef {z.infer<typeof creatingPartiesSchema>} TCreatingParties */
/** @typedef {z.infer<typeof savedPartySchema>} TSavedParty */
/** @typedef {z.infer<typeof savedPartiesSchema>} TSavedParties */
/** @typedef {Omit<TSavedParty,'id'>} TNewParty */
/** @typedef {TNewParty[]} TNewParties */
/** @typedef {TNewParty|TSavedParty} TNewOrSavedParty */
/** @typedef {TNewOrSavedParty[]} TNewOrSavedParties */
/** @typedef {z.infer<typeof partyUpdateSchema>} TPartyUpdate */
/** @typedef {z.infer<typeof partiesUpdateSchema>} TPartiesUpdate */
